import { EbayMessageForm } from "@/components/dashboard/integrations/ebay/ebay-message-form";
import { EbayOrderMessageTable } from "@/components/dashboard/integrations/ebay/ebay-message-orders";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import GatedOverlay from "@/components/ui/gated-overlay";
import {
  accountQueryOpts,
  ebayMessageConfigQueryOpts,
  ebayOrderMessagesQueryOpts,
} from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/$org/integrations/ebay/$alias/messaging",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId,
      },
      handleTokenInvalid,
    );

    const account = await context.queryClient.ensureQueryData(
      accountQueryOpts(params.alias, {
        setToken,
        token: getToken() as string,
        orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      ebayMessageConfigQueryOpts(account.platform_connection_id, {
        setToken,
        token: getToken() as string,
        orgId,
      }),
    );

    await context.queryClient.ensureQueryData(
      ebayOrderMessagesQueryOpts(account.platform_connection_id, {
        setToken,
        token: getToken() as string,
        orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org, alias } = Route.useParams();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { getToken, setToken } = useAuth();

  const { data: account } = useQuery(
    accountQueryOpts(alias, {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );

  const { data: config } = useQuery({
    ...ebayMessageConfigQueryOpts(account?.platform_connection_id ?? "", {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
    enabled: !!account?.platform_connection_id,
  });

  const { data: orderMessages } = useQuery({
    ...ebayOrderMessagesQueryOpts(account?.platform_connection_id ?? "", {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
    enabled: !!account?.platform_connection_id,
  });

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div className="mx-auto max-w-6xl">
      {account && config && (
        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>{t("integrations.messaging.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <EbayMessageForm config={config} />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <EbayOrderMessageTable
                config={config}
                orderMessages={orderMessages ?? []}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
